import { z } from "zod";

// Imported task/worker IDs can be legacy TEXT IDs, not only UUIDs.
const id = z.string().trim().min(1).max(200);
const ids = z.array(id).max(100).refine(value => new Set(value).size === value.length, "대상이 중복되었습니다.");
const instant = z.iso.datetime({ offset: true }).transform(value => new Date(value).toISOString());
const taskTargets = z.object({ projectIds: ids.default([]), taskIds: ids.default([]) }).strict()
  .refine(value => value.projectIds.length + value.taskIds.length > 0, "프로젝트 또는 작업을 선택해 주세요.");
const assignmentTargets = z.object({ workerIds: ids.min(1), tasks: taskTargets }).strict();

export const plannerRuleDefinitionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("worker_task_exclusion"), targets: assignmentTargets }).strict(),
  z.object({ kind: z.literal("worker_task_preference"), targets: assignmentTargets }).strict(),
  z.object({ kind: z.literal("task_not_before"), targets: taskTargets, at: instant }).strict(),
  z.object({ kind: z.literal("task_deadline"), targets: taskTargets, at: instant }).strict(),
  z.object({ kind: z.literal("worker_unavailable"), workerIds: ids.min(1) }).strict(),
]);

export const savePlannerRuleSchema = z.object({
  title: z.string().trim().min(1).max(120),
  originalText: z.string().trim().max(4000).default(""),
  definition: plannerRuleDefinitionSchema,
  strength: z.enum(["hard", "soft"]),
  weight: z.number().int().min(1).max(100).nullable().default(null),
  validFrom: instant.nullable().default(null),
  validUntil: instant.nullable().default(null),
}).strict().superRefine((value, ctx) => {
  const issue = (message: string, path: string[]) => ctx.addIssue({ code: "custom", message, path });
  if (value.validFrom && value.validUntil && value.validFrom >= value.validUntil)
    issue("종료 시각은 시작 시각보다 늦어야 합니다.", ["validUntil"]);
  if (value.strength === "soft" && value.weight === null) issue("선호 조건의 가중치를 지정해 주세요.", ["weight"]);
  if (value.strength === "hard" && value.weight !== null) issue("필수 조건에는 가중치를 지정할 수 없습니다.", ["weight"]);
  if (value.definition.kind === "worker_task_preference" && value.strength !== "soft")
    issue("우선 배정은 선호 조건으로 지정해 주세요.", ["strength"]);
  if (!["worker_task_preference", "task_deadline"].includes(value.definition.kind) && value.strength !== "hard")
    issue("이 규칙은 필수 조건으로 지정해 주세요.", ["strength"]);
  if (value.definition.kind === "worker_unavailable" && (!value.validFrom || !value.validUntil))
    issue("근무 불가 규칙에는 시작·종료 시각이 필요합니다.", ["validFrom"]);
});

export const plannerRuleVersionSchema = z.object({ expectedVersion: z.number().int().positive() }).strict();
export const updatePlannerRuleSchema = z.object({ expectedVersion: z.number().int().positive(), rule: savePlannerRuleSchema, interpretationId: z.string().uuid().optional() }).strict();
export const plannerRuleQuerySchema = z.object({
  state: z.enum(["draft", "approved", "disabled"]).optional(),
  projectId: id.optional(), workerId: id.optional(), taskId: id.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).max(100_000).default(0),
}).strict();

export type SavePlannerRule = z.input<typeof savePlannerRuleSchema>;
export type PlannerRuleDefinition = z.output<typeof plannerRuleDefinitionSchema>;
export type PlannerRuleState = "draft" | "approved" | "disabled";
export interface PlannerRuleActor { id: string; name: string }
export interface PlannerRule extends z.output<typeof savePlannerRuleSchema> {
  id: string;
  version: number;
  schemaVersion: 1;
  state: PlannerRuleState;
  origin: "manual" | "llm";
  interpretationId?: string | null;
  proposalId?: string | null;
  createdBy: PlannerRuleActor;
  updatedBy: PlannerRuleActor;
  approvedBy: PlannerRuleActor | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  periodStatus: "scheduled" | "current" | "expired";
  /** Eligibility, not a claim that a particular saved plan already uses this revision. */
  applicationStatus: "enabled" | "inactive" | "expired";
}
export interface PlannerRuleRevision {
  ruleId: string;
  version: number;
  action: "created" | "updated" | "approved" | "disabled";
  actor: PlannerRuleActor;
  recordedAt: string;
  snapshot: PlannerRule;
}
export interface PlannerRulePage { items: PlannerRule[]; total: number; offset: number; limit: number }
/** Immutable, approved business intent pinned to one calculation. No LLM text or credentials. */
export type PlanningOperatingRule = Pick<PlannerRule, "id" | "version" | "title" | "definition" | "strength" | "weight" | "validFrom" | "validUntil">;
export interface PlanningRuleSnapshot { revision: number; digest: string; rules: PlanningOperatingRule[] }

export function plannerRuleTargets(definition: PlannerRuleDefinition) {
  if (definition.kind === "worker_unavailable") return { workerIds: definition.workerIds, projectIds: [], taskIds: [] };
  if (definition.kind === "worker_task_exclusion" || definition.kind === "worker_task_preference")
    return { workerIds: definition.targets.workerIds, ...definition.targets.tasks };
  return { workerIds: [], ...definition.targets };
}

/** Half-open application interval, independent of approval and actual activation. */
export function plannerRulePeriod(rule: { validFrom: string | null; validUntil: string | null }, now = Date.now()): PlannerRule["periodStatus"] {
  if (rule.validUntil && Date.parse(rule.validUntil) <= now) return "expired";
  if (rule.validFrom && Date.parse(rule.validFrom) > now) return "scheduled";
  return "current";
}
